// Le battement vit dans `@ergo/shared` (`departureLeadMin`) : le flux iCal publié
// annonce la même heure de départ que cette timeline, et les deux doivent la
// calculer pareil.
import {
  type LocalDateTime,
  type LocalTime,
  type TravelLeg,
  departureLeadMin,
} from '@ergo/shared';
import { addMinutes, timeOf } from './time';

export interface Hop {
  /** The ride plus the buffer; null when the leg carries no estimate at all. */
  estimate: number | null;
  /** Leaving then, she arrives right at the start of the séance. */
  departure: LocalTime | null;
  /** The free time does not cover the ride — shown in red, never blocking. */
  tight: boolean;
  /** The minutes she actually has, when they are too few. */
  gapMin: number | null;
}

/** The leg arriving at a séance, i.e. the hop drawn just above it in a timeline. */
export function legInto(legs: TravelLeg[] | undefined, appointmentId: number): TravelLeg | null {
  return legs?.find((leg) => leg.toAppointmentId === appointmentId) ?? null;
}

/**
 * The ride home closing the day, when there is one to ride: a day ending at home —
 * a visio — has a leg of zero minutes, and nothing to show for it.
 */
export function legHome(legs: TravelLeg[] | undefined): TravelLeg | null {
  const leg = legs?.find((candidate) => candidate.toHome) ?? null;
  return leg && leg.bikeMinutes !== 0 ? leg : null;
}

/**
 * What a timeline shows above a séance: when to leave, and whether that is at all
 * realistic. The hop out of « Maison » has no previous séance to be late for, and
 * the server marks it with a null gap: never tight.
 */
export function hopOf(leg: TravelLeg | null, startsAt: LocalDateTime): Hop {
  // Zero minutes means both ends are the same door — a visio at home: no hop at all.
  const estimate = departureLeadMin(leg?.bikeMinutes ?? null);
  const gapMin = leg?.gapMin ?? null;
  const tight = estimate !== null && gapMin !== null && gapMin < estimate;

  return {
    estimate,
    departure: estimate === null ? null : timeOf(addMinutes(startsAt, -estimate)),
    tight,
    gapMin: tight ? gapMin : null,
  };
}
